import type { AgentMemory } from "./agentMemory";
import type { StreamOptions, StreamOutcome, StreamRequest } from "./agentStream";
import { buildAnswerUserMessage } from "./answerMessage";
import { buildAnswerSystemPrompt } from "./systemPrompt";

export type AnswerStreamDeps = {
  question: string;
  toolContext: string[];
  usedToolNames: string[];
  plannerReply: string | null;
  memory: AgentMemory;
  model: string | undefined;
  timeoutMs: number;
  stream: (request: StreamRequest, handlers: { onDelta: (accumulated: string) => void }, options: StreamOptions) => Promise<StreamOutcome>;
  onDelta: (accumulated: string) => void;
};

// Stream the synthesized answer (one no-tools completion over the turn's tool results) and write it
// back into conversation memory so a restored conversation renders the same reply. A dropped stream
// keeps whatever text arrived; with nothing delivered the planner's own reply, then a generic
// fallback, keeps the turn honest.
export const streamAnswer = async (deps: AnswerStreamDeps): Promise<string> => {
  const outcome = await deps.stream(
    {
      messages: [
        { role: "system", content: buildAnswerSystemPrompt() },
        {
          role: "user",
          content: buildAnswerUserMessage({
            question: deps.question,
            toolContext: deps.toolContext,
            plannerReply: deps.plannerReply,
          }),
        },
      ],
      model: deps.model,
    },
    { onDelta: deps.onDelta },
    { timeoutMs: deps.timeoutMs },
  );

  const answerText = outcome.text.trim() ? outcome.text : null;
  if (answerText !== null) {
    if (deps.plannerReply !== null) {
      deps.memory.replaceTrailingAssistantReply(answerText);
    } else {
      deps.memory.pushAssistantReply(answerText);
    }
    return answerText;
  }
  if (deps.plannerReply !== null) {
    return deps.plannerReply;
  }
  const fallback = deps.usedToolNames.length
    ? `Svarte brukeren basert på: ${deps.usedToolNames.join(", ")}`
    : "Svarte brukeren.";
  deps.memory.pushAssistantReply(fallback);
  return fallback;
};
